import { installCsrf } from './csrf';
import { bindInstall, refreshInstallHint } from './install';
import { bindShare } from './share';
import { bindNotaSse } from './sse';
import { pinEmitirActionBar } from './visual-viewport';
import { bindDraftAutosave } from './drafts';
import { bindTakerCombobox } from './tomadores';
import { bindCompanyLookup } from './empresas';
import { bindLoginTrails } from './login-trails';
import { bindMasks } from './masks';
import { bindDatePickers, bindFilePickers, bindMonthPickers, bindSelects, bindServiceCombobox } from './componentes';

function bindDrawers(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-drawer-trigger]').forEach((trigger) => {
    if (trigger.dataset.bound === 'true') return;
    const dialogID = trigger.getAttribute('aria-controls');
    const dialog = dialogID ? document.getElementById(dialogID) : null;
    if (!(dialog instanceof HTMLDialogElement)) return;

    trigger.dataset.bound = 'true';
    trigger.addEventListener('click', () => {
      if (!dialog.open) {
        dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
    dialog.querySelector<HTMLButtonElement>('[data-drawer-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    });
    dialog.addEventListener('toggle', () => {
      trigger.setAttribute('aria-expanded', dialog.open ? 'true' : 'false');
    });
  });
}

// Empresa ativa: escolher outra no seletor já envia o formulário.
function bindAutoSubmit(): void {
  document.querySelectorAll<HTMLFormElement>('form[data-auto-submit]').forEach((form) => {
    if (form.dataset.bound === 'true') return;
    form.dataset.bound = 'true';
    form.addEventListener('change', () => form.requestSubmit());
  });
}

// Barra lateral: recolhe até só os ícones. A preferência fica num cookie para o servidor já
// desenhar a página no mesmo estado, sem piscar entre telas. Recolhida, o botão some e a marca
// passa a expandir a barra em vez de ir ao início.
function setSidebar(recolhida: boolean): void {
  document.body.classList.toggle('sidebar-recolhida', recolhida);
  const toggle = document.querySelector<HTMLButtonElement>('[data-sidebar-toggle]');
  if (toggle) {
    const label = recolhida ? 'Expandir menu lateral' : 'Recolher menu lateral';
    toggle.setAttribute('aria-expanded', String(!recolhida));
    toggle.setAttribute('aria-label', label);
    toggle.title = label;
  }
  const marca = document.querySelector<HTMLElement>('[data-sidebar-expand]');
  marca?.setAttribute('aria-label', recolhida ? 'Expandir menu lateral' : rotuloDaMarca(marca));
  document.cookie = `jnr_sidebar=${recolhida ? 'recolhida' : 'aberta'}; path=/; max-age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

function bindSidebarToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>('[data-sidebar-toggle]');
  if (!toggle || toggle.dataset.bound === 'true') return;
  toggle.dataset.bound = 'true';
  toggle.addEventListener('click', () => {
    setSidebar(true);
    document.querySelector<HTMLElement>('[data-sidebar-expand]')?.focus();
  });
  document.querySelector<HTMLAnchorElement>('[data-sidebar-expand]')?.addEventListener('click', (event) => {
    // Só a barra recolhida no desktop (botão visível só lá) troca o destino da marca.
    if (!document.body.classList.contains('sidebar-recolhida') || !matchMedia('(min-width: 1024px)').matches) return;
    event.preventDefault();
    setSidebar(false);
    toggle.focus();
  });
  // No celular a marca sempre leva ao início, mesmo com a preferência de barra recolhida.
  if (!matchMedia('(min-width: 1024px)').matches) {
    const marca = document.querySelector<HTMLElement>('[data-sidebar-expand]');
    marca?.setAttribute('aria-label', rotuloDaMarca(marca));
  }
}

// O admin leva a marca ao painel administrativo; o rótulo vem do próprio link.
function rotuloDaMarca(marca: HTMLElement | null): string {
  return marca?.dataset.rotulo ?? 'JNR — início';
}

// Olho do campo de senha: alterna password/text. Sem JS o botão fica oculto e o campo segue normal.
function bindPasswordToggles(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-password-toggle]').forEach((btn) => {
    if (btn.dataset.bound === 'true') return;
    const input = document.getElementById(btn.getAttribute('aria-controls') ?? '');
    if (!(input instanceof HTMLInputElement)) return;
    btn.dataset.bound = 'true';
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.setAttribute('aria-pressed', String(show));
      btn.setAttribute('aria-label', show ? 'Ocultar senha' : 'Mostrar senha');
    });
    // Senha nunca vai em texto claro para o envio nem para o gerenciador de senhas.
    input.form?.addEventListener('submit', () => {
      input.type = 'password';
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Mostrar senha');
    });
  });
}

function onPage(): void {
  pinEmitirActionBar();
  bindNotaSse();
  bindDraftAutosave();
  bindTakerCombobox();
  bindServiceCombobox();
  bindSelects();
  bindMonthPickers();
  bindDatePickers();
  bindFilePickers();
  bindMasks();
  bindCompanyLookup();
  bindDrawers();
  bindPasswordToggles();
  bindAutoSubmit();
  bindSidebarToggle();
  bindLoginTrails();
  document.querySelector<HTMLElement>('[data-customer-form] [aria-invalid="true"], [data-service-form] [aria-invalid="true"], [data-certificate-form] [aria-invalid="true"], [data-company-form] [aria-invalid="true"], [data-taker-combobox] [aria-invalid="true"]')?.focus();
  refreshInstallHint();
}

function boot(): void {
  installCsrf();
  bindShare();
  bindInstall();
  onPage();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('htmx:afterSwap', onPage);
document.addEventListener('htmx:historyRestore', onPage);
